/**
 * Live model spend: the one identity-only broadcast that tells a dashboard the
 * platform's call record moved, delivered through the shared `global_events`
 * room (TODO-2027).
 *
 * The room has exactly one entry point, a client-emitted `subscribe_global`,
 * and `websocketManager.subscribe()` only attaches a local handler — it never
 * sends the join and never replays it after a reconnect — so a spend surface
 * subscribes through this domain or receives nothing.
 */

// The leaf rather than a barrel: MODEL_SPEND_EVENTS is a runtime value.
import {
  MODEL_SPEND_EVENTS,
  type ModelSpendEventName,
  type ModelSpendRecordedPayload,
} from '@/types/modelSpend'
import { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'

type ModelSpendDispatch = [ModelSpendEventName, ModelSpendRecordedPayload]

export class ModelSpendLiveApi {
  private readonly events: SubscriptionRegistry<null, ModelSpendDispatch>

  constructor(private readonly client: WebSocketClient) {
    this.events = new SubscriptionRegistry({
      // Joins the shared `global_events` room; subscribe_global is idempotent
      // server-side. There is deliberately no leave — other domains share the
      // room, and leaving would silence their subscribers.
      join: () => this.client.emitWhenConnected('subscribe_global', {}),
    })
  }

  /** Subscribe to the call record's broadcast; the callback receives its name and payload. */
  subscribeToModelSpend(
    callback: (event: ModelSpendEventName, payload: ModelSpendRecordedPayload) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    for (const event of MODEL_SPEND_EVENTS) {
      this.client.attachOnce(event, (raw) => {
        // Identity-only routing data that is never rendered, so narrowing by
        // event name is the whole contract.
        this.events.dispatch(null, [event, raw as ModelSpendRecordedPayload])
      })
    }

    return this.events.subscribe(null, ([name, payload]) => callback(name, payload))
  }

  /** Re-join global_events for every live listener after a reconnect. */
  replay(): void {
    this.events.replay()
  }

  clear(): void {
    this.events.clear()
  }
}
